package org.vreo.devicehub.features.device.presentation.deviceManagement.messages.components

import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

import androidx.compose.foundation.Image
import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.stringResource

import org.vreo.devicehub.features.core.domain.device.message.AnprCameraMessage
import org.vreo.devicehub.features.core.presentation.getFormatedDate
import org.vreo.devicehub.features.core.presentation.getFormatedTime
import org.vreo.devicehub.features.core.presentation.roundToDecimals

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.message_details_image_not_available_title
import devicehub.composeapp.generated.resources.message_details_device_title
import devicehub.composeapp.generated.resources.message_item_date_title
import devicehub.composeapp.generated.resources.message_details_anpr_title
import devicehub.composeapp.generated.resources.message_details_hardware_title
import devicehub.composeapp.generated.resources.message_details_enclosure_title
import devicehub.composeapp.generated.resources.message_details_vehicle_id
import devicehub.composeapp.generated.resources.message_details_license_plate
import devicehub.composeapp.generated.resources.message_details_confidence_score
import devicehub.composeapp.generated.resources.message_details_ocr_attempt
import devicehub.composeapp.generated.resources.message_details_license_plate_width
import devicehub.composeapp.generated.resources.message_details_license_plate_height
import devicehub.composeapp.generated.resources.message_details_ram_usage
import devicehub.composeapp.generated.resources.message_details_cpu_usage
import devicehub.composeapp.generated.resources.message_details_cpu_temperature
import devicehub.composeapp.generated.resources.message_details_enclosure_temperature
import devicehub.composeapp.generated.resources.message_details_enclosure_pressure
import devicehub.composeapp.generated.resources.message_details_enclosure_humidity
import devicehub.composeapp.generated.resources.message_details_ocr_version_title
import devicehub.composeapp.generated.resources.message_details_software_version_title

/**
 * Composable for displaying message details and image.
 *
 * @param message The message to display.
 * @param image The image associated with the message. Default to null.
 * @param modifier The modifier for the composable.
 */
@Composable
fun MessageDetails(
    deviceName: String,
    message: AnprCameraMessage,
    image: ImageBitmap?,
    modifier: Modifier = Modifier,
) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(16.dp),
        modifier = modifier
            .fillMaxSize()
    ) {
        Box(
            contentAlignment = Alignment
                .Center,
            modifier = Modifier
                .weight(0.8f)
                .padding(8.dp)
        ) {
            if (image == null) {
                Text(
                    text = stringResource(Res.string.message_details_image_not_available_title),
                    style = MaterialTheme.typography.displaySmall,
                )
            } else {
                Image(
                    bitmap = image,
                    contentDescription = null,
                    contentScale = ContentScale.Fit,
                )
            }
        }

        Details(
            deviceName = deviceName,
            message = message,
            modifier = Modifier
                .weight(0.2f)
                .padding(8.dp)
        )
    }
}

/**
 * Details composable for displaying message details
 *
 * @param deviceName name of the device
 * @param message message to display
 * @param modifier modifier for the composable
 * @param scrollState scroll state for the composable
 */
@Composable
private fun Details(
    deviceName: String,
    message: AnprCameraMessage,
    modifier: Modifier = Modifier,
    scrollState: ScrollState = rememberScrollState()
) {
    val infoTitleStyle: TextStyle = MaterialTheme.typography.bodySmall.copy(
        fontWeight = FontWeight.Bold
    )

    Column(
        horizontalAlignment = Alignment.Start,
        verticalArrangement = Arrangement.spacedBy(8.dp),
        modifier = modifier
            .fillMaxWidth()
            .verticalScroll(scrollState)
    ) {
        // Device name
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = "${stringResource(Res.string.message_details_device_title)}:",
                style = infoTitleStyle,
            )
            Text(
                text = deviceName,
                style = infoTitleStyle,
            )
        }

        // Software version
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = "${stringResource(Res.string.message_details_software_version_title)}:",
                style = infoTitleStyle,
            )
            Text(
                text = message.softwareVersion,
                style = infoTitleStyle,
            )
        }

        // OCR version
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = "${stringResource(Res.string.message_details_ocr_version_title)}:",
                style = infoTitleStyle,
            )
            Text(
                text = message.ocrVersion,
                style = infoTitleStyle,
            )
        }

        // Message date and time
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = stringResource(Res.string.message_item_date_title),
                style = infoTitleStyle,
            )
            Text(
                text = formatDateAndTime(message.date),
                style = infoTitleStyle,
            )
        }

        // Message details
        Row(
            horizontalArrangement = Arrangement.spacedBy(32.dp),
        ) {
            // ANPR details
            Column(
                horizontalAlignment = Alignment.Start,
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(
                    text = stringResource(Res.string.message_details_anpr_title),
                    style = infoTitleStyle,
                )
                Row(
                    horizontalArrangement = Arrangement.spacedBy(32.dp),
                ) {
                    Column(
                        horizontalAlignment = Alignment.Start,
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        DetailsTextField(
                            label = stringResource(Res.string.message_details_vehicle_id),
                            value = message.vehicleId.toString(),
                        )
                        DetailsTextField(
                            label = stringResource(Res.string.message_details_license_plate),
                            value = message.licensePlate,
                        )
                        DetailsTextField(
                            label = stringResource(Res.string.message_details_confidence_score),
                            value = message.confidenceScore.roundToDecimals().toString(),
                        )
                    }

                    Column(
                        horizontalAlignment = Alignment.Start,
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        DetailsTextField(
                            label = stringResource(Res.string.message_details_ocr_attempt),
                            value = message.ocrAttempt.toString(),
                        )
                        DetailsTextField(
                            label = stringResource(Res.string.message_details_license_plate_width),
                            value = message.licensePlateWidth.toString(),
                        )
                        DetailsTextField(
                            label = stringResource(Res.string.message_details_license_plate_height),
                            value = message.licensePlateHeight.toString(),
                        )
                    }
                }
            }

            // Hardware details
            Column(
                horizontalAlignment = Alignment.Start,
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(
                    text = stringResource(Res.string.message_details_hardware_title),
                    style = infoTitleStyle,
                )
                DetailsTextField(
                    label = stringResource(Res.string.message_details_ram_usage),
                    value = getAverageValue(message.telemetry.map { it.ramUsage }),
                )
                DetailsTextField(
                    label = stringResource(Res.string.message_details_cpu_usage),
                    value = getAverageValue(message.telemetry.map { it.cpuUsage }),
                )
                DetailsTextField(
                    label = stringResource(Res.string.message_details_cpu_temperature),
                    value = getAverageValue(message.telemetry.map { it.cpuTemperature }),
                )
            }

            // Device enclosure details
            Column(
                horizontalAlignment = Alignment.Start,
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(
                    text = stringResource(Res.string.message_details_enclosure_title),
                    style = infoTitleStyle,
                )
                DetailsTextField(
                    label = stringResource(Res.string.message_details_enclosure_temperature),
                    value = getAverageValue(message.telemetry.map { it.enclosureTemperature }),
                )
                DetailsTextField(
                    label = stringResource(Res.string.message_details_enclosure_pressure),
                    value = getAverageValue(message.telemetry.map { it.enclosurePressure }),
                )
                DetailsTextField(
                    label = stringResource(Res.string.message_details_enclosure_humidity),
                    value = getAverageValue(message.telemetry.map { it.enclosureHumidity }),
                )
            }
        }
    }
}

/**
 * Details text field composable. Displays label and value.
 *
 * @param label label for the field
 * @param value value for the field
 * @param labelStyle style for the label
 * @param valueStyle style for the value
 */
@Composable
private fun DetailsTextField(
    label: String,
    value: String,
    labelStyle: TextStyle = MaterialTheme.typography.bodyMedium,
    valueStyle: TextStyle = MaterialTheme.typography.bodyMedium
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = "$label:",
            style = labelStyle,
        )
        Text(
            text = value,
            style = valueStyle,
        )
    }
}

/**
 * Get average value from list of floats.
 *
 * @param values list of floats
 * @return average value as string
 */
private fun getAverageValue(values: List<Float>): String {
    return try {
        values.average().roundToDecimals().toString()
    } catch (_: Exception) {
        "N/A"
    }
}

/**
 * Format date and time to string.
 *
 * @param date date to format
 * @return formated date and time as string
 */
private fun formatDateAndTime(date: LocalDateTime): String {
    return "${getFormatedDate(date)}  ${getFormatedTime(date)}"
}

@Preview
@Composable
fun MessageDetailsPreview() {
    @OptIn(ExperimentalTime::class) val currentMoment: Instant = Clock.System.now()

    @OptIn(ExperimentalTime::class) val datetimeInSystemZone: LocalDateTime =
        currentMoment.toLocalDateTime(TimeZone.currentSystemDefault())

    MaterialTheme {
        MessageDetails(
            deviceName = "device",
            message = AnprCameraMessage(
                msgId = "1",
                softwareVersion = "1.0.0",
                ocrVersion = "1.0.0",
                vehicleId = 1,
                licensePlate = "abc 123",
                licensePlateWidth = 1,
                licensePlateHeight = 1,
                confidenceScore = 1f,
                ocrAttempt = 1,
                imageUrl = "/example/url",
                date = datetimeInSystemZone,
                timeStamp = 0,
                selected = false
            ),
            image = null,
            modifier = Modifier.background(
                color = MaterialTheme.colorScheme.background
            )
        )
    }
}